import { Component, AfterViewInit, OnInit, Renderer2, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { EngineVisibilityEnum } from 'src/app/engine/models/engine-visibility.enum';
import { 
  SetEnginePosition,
  ClickNode,
  SetActiveTool, 
  SetDisplay,
  PlayAnimation} from 'src/app/engine/state/engine.actions';
import { LoadStory, SetIncomingStory, LoadStoryFail, AddToStory } from 'src/app/story/state/story.actions';
import { StoryState } from 'src/app/story/state/story.state';
import { Observable, Subject, combineLatest, BehaviorSubject } from 'rxjs';
import { StoryModel } from 'src/app/story/models/story.model';
import { filter, takeUntil, map, take, delay, first, tap, pairwise } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { EngineState } from 'src/app/engine/state/engine.state';
import { SetTheme } from 'src/app/layout/state/layout.actions';
import { LayoutThemeEnum } from 'src/app/layout/models/layout-theme.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { BdGraph } from 'src/lib/graph/graph.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewState } from '../../state/view.state';
import { AnimationService } from 'src/app/engine/animation.service';
import { SelectBookmark, SelectBookmarkSuccess } from 'src/app/bookmarks/state/bookmarks.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { BookmarkActionsEnum } from 'src/app/bookmarks/components/bookmark-actions/bookmark-actions.component';
import { LockCamera, SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { SelectChapter, RecordChapterVisibilityState, NextChapter, PreviousChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { 
  SetCheckedNodes,
  CheckNode,
  SelectNode,
  ExpandNode,
  ToggleVisibleNodes,
  ResetEngineNodes,
  Isolate 
} from 'src/app/engine/state/nodes/engine.nodes.actions';
import { HistoryService } from 'src/app/history/history.service';
import { 
  CacheChapterSynchronization,
  ClearHistory,
  ResetHistory,
  CopyChapterSynchronization } from 'src/app/history/state/history.actions';
import * as _ from 'lodash';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { ExploreService } from 'src/app/explore/explore.service';
import { UserProfileModel } from 'src/app/auth/models/user-profile.model';
import { EnableContextLabel, ConvertLabels, ResetLabels, SetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { PaintService } from 'src/app/engine/paint.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelRecipe } from 'src/app/story/models/labels/label-recipe.enum';
import { ResetView, ShowColumn } from '../../state/view.actions';
import { PaintPresetFade } from 'src/app/engine/models/paint-presets.const';
import { TreeTheme } from 'src/app/shared/components/tree/tree/tree.component';
import { FormControl } from '@angular/forms';
import { SetObjectVisibility } from 'src/app/edit/state/edit.actions';
import { StoryService } from 'src/app/story/story.service';

declare const Human: any;

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewComponent implements OnInit, OnDestroy, AfterViewInit {
  treeTheme = TreeTheme;
  bookmarkActions = BookmarkActionsEnum;

  @ViewChild('engineContainer')
  engineContainer: ElementRef;

  @ViewChild('searchInput')
  searchInput: ElementRef<HTMLInputElement>;

  // Bookmark
  @Select(BookmarksState.getSelectedBookmark)
  bookmark$: Observable<BookmarkModel>;

  // Story
  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  @Select(StoryState.getIncomingStoryInfo)
  incoming$: Observable<{id: string, type: StoryType}>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(StoryChaptersState.getStoryChapters)
  storyChapters$: Observable<ChapterModel>;

  // Engine
  @Select(EngineState.getReady)
  engineReady$: Observable<boolean>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  // View
  @Select(ViewState.getShowPanel)
  showPanel$: Observable<boolean>;

  @Select(ViewState.getShowColumn)
  showColumn$: Observable<boolean>;

  // Profile
  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  isAnimated = false;
  interactive = false;
  animationReady = false;
  showScrubber = false;
  showPlayPause = false;

  id: string;
  type: StoryType;
  category: string;
  query = '';
  selectedColumnGroup = 'Description';

  windowSize$ = new BehaviorSubject(window.innerWidth);
  searchControl = new FormControl('');
  
  private ngUnsubscribe = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private store: Store,
    private actions: Actions,
    private location: Location,
    private animationService: AnimationService,
    private historyService: HistoryService,
    private exploreService: ExploreService,
    private dialogService: DialogService,
    private paintService: PaintService,
    private storyService: StoryService,
    private router: Router  ) { }

  ngOnInit() {
    this.initListeners();
    this.initKeyboardListeners();

    this.store.dispatch(new SetTheme(LayoutThemeEnum.Light));
    this.store.dispatch(new ResetLabels());
    this.store.dispatch(new ResetView());
    this.store.dispatch(new ResetHistory());
    this.store.dispatch(new SetActiveTool(ToolEnum.Select));
    this.store.dispatch(new LockCamera(false));
    this.store.dispatch(new SetDisplay(true));
    this.store.dispatch(new EnableContextLabel(true));

    const { id } = this.activatedRoute.snapshot.queryParams;
    this.id = this.sanitizeRouteId(id);
    this.type = this.storyService.getStoryType(this.id);


    this.store.dispatch(new SetIncomingStory(this.id, this.type, true));
    // set story metadata
    this.store.dispatch(new SelectBookmark(this.id, this.type));
  }

  ngOnDestroy() {
    // #TODO: Put theme management in route
    // Change the theme
    this.store.dispatch(new SetTheme(LayoutThemeEnum.Light));
    this.store.dispatch(new ResetEngineNodes());
    this.store.dispatch(new SetDisplay(false));

    this.unbindKeyboardListeners();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkColumnVisible();
      this.resizeEngineContainer();
    }, 0);

    // Choose default column. Don't default to description when it's empty.
    this.selectedChapter$.pipe(
      filter((chapter) => chapter !== null),
      first(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapter: ChapterModel) => {
      this.chooseColumn(chapter);
    });
  }

  initListeners() {
    // Only check column visibility when we pass the responsive breakpoint
    this.windowSize$.pipe(
      pairwise(),
      filter(([oldWidth, newWidth]) => {
        return (
          (oldWidth <= 768 && newWidth > 768) ||
          (oldWidth >= 768 && newWidth < 768)
        );
      })
    ).subscribe(([oldWidth, newWidth]) => {
      this.checkColumnVisible();
    });

    // Window has resized, let the 3D canvas know
    this.renderer.listen('window', 'resize', () => {
      this.windowSize$.next(window.innerWidth);
      this.resizeEngineContainer();
    });

    combineLatest(this.engineReady$, this.incoming$).pipe(
      filter(([ready, incoming]) => ready && !!incoming.id),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.store.dispatch(new SelectChapter()); // null chapter state
      this.loadStory();
    });

    this.searchControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(query => {
        this.query = query;
      });

    this.bookmark$
      .pipe(
        filter(bookmark => bookmark != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(bookmark => {
        this.getCategory(bookmark);
      });

    combineLatest(this.bookmark$, this.userProfile$)
      .pipe(
        filter(([bookmark, profile]) => {
          return bookmark != null && profile != null;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([bookmark, profile]) => {
        this.handlePremiumContent(bookmark, profile);
      });

    // Listen for clicked nodes
    this.actions
      .pipe(
        ofActionDispatched(ClickNode),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => {
        this.handleNodeClicked(action.node, action.event);
      });

    this.selectedChapter$.pipe(
        filter((chapter) => chapter != null), 
        takeUntil(this.ngUnsubscribe)
      ).subscribe((chapter) => {
        this.interactive = chapter.readyState.model;
        this.handleAnimationReady(chapter);
      });

    this.selectedChapterId$
      .pipe(
        filter(chapter => chapter != null),
        map(() => this.store.selectSnapshot(StoryChaptersState.getSelectedChapter)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        this.handleChapterChange(chapter);
      });

    this.actions
      .pipe(
        ofActionDispatched(ToggleVisibleNodes),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: ToggleVisibleNodes) => {
        if (action.recordHistory) {
          this.historyService.recordViewGraphHistory();
        }
      });

    // content not found
    this.actions
      .pipe(
        ofActionDispatched(LoadStoryFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new SetIncomingStory(null, null));
        
        this.router.navigate(['/404', ], {
          queryParams: {
            errorType: 'content',
            errorUrl: this.router.url
          },
          skipLocationChange: false,
          replaceUrl: true
        });
      });
  }

  initKeyboardListeners() {
    // Tool switching
    Mousetrap.bind('1', () => this.store.dispatch(new SetActiveTool(ToolEnum.Select)));
    Mousetrap.bind('2', () => this.store.dispatch(new SetActiveTool(ToolEnum.Hide)));
    Mousetrap.bind('3', () => this.store.dispatch(new SetActiveTool(ToolEnum.Fade)));

    // Column toggles
    Mousetrap.bind('meta+,', (event) => {
      event.preventDefault();
      this.collapseColumn();
    });

    // Chapter browser
    Mousetrap.bind('.', () => this.store.dispatch(new NextChapter()));
    Mousetrap.bind(',', () => this.store.dispatch(new PreviousChapter()));

    // Delete
    Mousetrap.bind(['del', 'backspace'], () => this.handleDelete());
  }

  unbindKeyboardListeners() {
    Mousetrap.unbind(['1', '2', '3', 'meta+,', '.', ',', 'del', 'backspace']);
  }

  getCategory(bookmark) {
    this.category = this.exploreService.getBookmarkCategory(bookmark);
  }

  handlePremiumContent(bookmark, profile) {
    if (bookmark.premium && !profile.premium_account_valid) {
      const dialogRef = this.dialogService.openPremiumContentDialog();
      dialogRef.afterClosed()
        .pipe(
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(confirmed => {
          if (confirmed) {
            this.dialogService.openUpgradeDialog();
          }
          // always redirect
          this.router.navigate(['/dashboard']);
        });
    }
  }

  handleDelete() {
    const selectedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);
    this.store.dispatch(new SetObjectVisibility(selectedNodes, false));
  }

  chooseColumn(chapter: ChapterModel) {
    // Ensure we select a default column
    if (!this.selectedColumnGroup) {
      this.selectedColumnGroup = 'Description';
    }

    // Don't show description column if it's empty
    if (this.selectedColumnGroup === 'Description' && chapter.info.description.length === 0) {
      this.selectedColumnGroup = 'Anatomy';
    }
  }

  checkColumnVisible() {
    setTimeout(() => {
      const showColumn = (window.innerWidth > 768);
      this.store.dispatch(new ShowColumn(showColumn));
    });
  }

  resizeEngineContainer() {
    // Placed inside a timeout to let any conditional visibility of elements permeate first. This way,
    // engine is more likely to measure the accurate visible space it belongs to.
    setTimeout(() => {
      const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = this.engineContainer.nativeElement;
      const position = new EnginePositionModel(offsetLeft, offsetTop, offsetWidth, offsetHeight, EngineVisibilityEnum.Shown);
      this.store.dispatch(new SetEnginePosition(position));

    });
  }

  loadStory() {
    const {id, type} = this.store.selectSnapshot(StoryState.getIncomingStoryInfo);
    this.store.dispatch(new LoadStory(id, type)).subscribe();
  }

  /**
   * Handlers
   */
  handleChapterChange(chapter: ChapterModel) {
    this.store.dispatch(new SetCamera(chapter.camera.location));

    this.store.dispatch(new CacheChapterSynchronization('root', chapter.id, chapter.synchronization, true));
    this.store.dispatch(new CopyChapterSynchronization('root', chapter.id, 'cache', chapter.id));
    
    this.viewGraph$
      .pipe(
        take(1),
        delay(1)
      ).subscribe(() => {
        this.clearHistory();
      });
  }

  handleAnimationReady(chapter) {
    this.animationReady = chapter.readyState.animation;
    this.isAnimated = this.animationService.isChapterAnimated(chapter);

    if (this.isAnimated && this.animationReady) {
      this.store.dispatch(new PlayAnimation(true));
    }
  }

  handleNodeClicked(node: ViewTreeNode, event: HammerInput) {
    if (!node && !event.srcEvent.shiftKey) {
      this.store.dispatch(new SetCheckedNodes([]));

    } else if (event.srcEvent.shiftKey) {
      const checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(value => value.id === node.id);

      this.store.dispatch(new CheckNode(node, !checked));
      this.store.dispatch(new SelectNode(node));

    } else {
      const tool = this.store.selectSnapshot(EngineState.getActiveTool);
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      
      switch (tool) {
        case ToolEnum.Hide:
          this.handleToggleVisibleNodes({nodes: [node], visible: false});
          break;
        case ToolEnum.Select:
          const checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(value => value.id === node.id);
          
          (checked) ? 
            this.store.dispatch(new SetCheckedNodes([])) :
            this.store.dispatch(new SetCheckedNodes([node]));
            
          this.store.dispatch(new SelectNode(node));
          this.store.dispatch(new ExpandNode(node));
          break;
        case ToolEnum.Isolate:
          this.store.dispatch(new Isolate([node], viewGraph));
          break;
        case ToolEnum.Fade:
          this.paintService.paint(node, PaintPresetFade.recipe);
          break;
      }
    }
  }
  
  handleToggleVisibleNodes(event: { nodes: ViewTreeNode[], visible?: boolean }) {
    this.store.dispatch(new ToggleVisibleNodes(event.nodes, event.visible));
    this.store.dispatch(new SetCheckedNodes([]));

    this.store.dispatch(new RecordChapterVisibilityState(event.nodes, event.visible, true));
  }

  handleCollapseClick() {
    this.collapseColumn();
  }

  collapseColumn() {
    const showColumn = this.store.selectSnapshot(ViewState.getShowColumn);
    this.store.dispatch(new ShowColumn(!showColumn));

    const interval = setInterval(() => {
      this.resizeEngineContainer();
    }, 20);

    setTimeout(() => {
      clearInterval(interval);
    }, 300);
  }

  handleSearchKeyup(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.clearSearchQuery();
      this.searchInput.nativeElement.blur();
    }
  }

  clearHistory() {
    this.store.dispatch(new ClearHistory());
    this.historyService.recordViewGraphHistory();
  }

  clearSearchQuery() {
    this.searchControl.setValue('');
  }

  // Bookmark routes use appropriate ID parameters, but internal users would like the ability to paste
  // in legacy paths. This means stripping out the .json extension at time of page load and accounting
  // for that conversion later on. 
  private sanitizeRouteId(id: string): string {
    id = id.replace(/\.json$/, '');
    const query = `?id=${id}`;
    this.location.replaceState('view', query);
    return id;
  }
}
